import { EligibilityRuleDto, QuestionDto, SurveyQuestionsResponse } from "api/survey";
import { GenericOperator } from "enums/genericOperator";
import { EligibilityRule, GenericQuestionGroup, PossibleAnswer, Question, QuestionGroup, StepConstraint } from "features/subscription-process/profiling-process/types/interfaces";

/**
 * Maps StepConstraintDto to StepConstraint
 */
function mapStepConstraint(constraint: any): StepConstraint {
  return {
    questionKey: constraint.questionKey || '',
    operator: constraint.operator || 'equals',
    value: constraint.value,
    values: constraint.values ? convertValuesToStringOrNumber(constraint.values) : undefined
  };
}

/**
 * Maps PossibleAnswerDto to PossibleAnswer
 */
function mapPossibleAnswer(answer: any): PossibleAnswer {
  if (!answer) return '';

  // Si c'est un objet avec key et label
  if (answer.key !== undefined && answer.label !== undefined) {
    // Avec icône
    if (answer.iconKey) {
      return {
        id: answer.id,
        key: answer.key,
        label: answer.label,
        iconKey: answer.iconKey
      };
    }
    // Simple avec label
    return {
      id: answer.id,
      key: answer.key,
      label: answer.label
    };
  }

  // Si c'est une valeur primitive
  return answer;
}

/**
 * Converts object array to string/number array
 */
function convertValuesToStringOrNumber(values: any[]): (string | number)[] {
  return values.map(value => {
    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }
    if (typeof value === 'object' && value !== null) {
      // Si l'objet a une propriété 'key', l'utiliser
      if ('key' in value) {
        return value.key;
      }
      // Si l'objet a une propriété 'value', l'utiliser
      if ('value' in value) {
        return value.value;
      }
      // Sinon, convertir en string
      return JSON.stringify(value);
    }
    // Fallback: convertir en string
    return String(value);
  });
}

/**
 * Maps QuestionDto to Question
 */
function mapQuestion(questionDto: QuestionDto): Question {
  return {
    id: questionDto.id,
    key: questionDto.key || '',
    label: questionDto.label || '',
    isMultipleChoice: questionDto.questionTypeCode === 'multiple_choice',
    isRequired: questionDto.isRequired ?? true,
    defaultValue: questionDto.defaultValue ?? undefined,
    possibleAnswers: questionDto.possibleAnswers?.map(mapPossibleAnswer) || [],
    type: questionDto.questionTypeCode,
    typeLabel: questionDto.questionTypeLabel,
    dependsOn: questionDto.dependsOn && questionDto.dependsOn.length > 0 ? {
      questionKey: questionDto.dependsOn[0].questionKey || '',
      values: convertValuesToStringOrNumber(questionDto.dependsOn[0].values || [])
    } : undefined
  };
}

/**
 * Groups questions by step number
 */
function groupQuestionsByStep(questions: QuestionDto[]): Map<number, QuestionDto[]> {
  const grouped = new Map<number, QuestionDto[]>();

  questions.forEach(question => {
    const step = question.step ?? 1; // Default to step 1 if no step defined
    if (!grouped.has(step)) {
      grouped.set(step, []);
    }
    grouped.get(step)!.push(question);
  });

  return grouped;
}

/**
 * Maps SurveyQuestionsResponse to QuestionGroup[]
 */
export function mapSurveyQuestionsToQuestionGroups(
  surveyResponse: SurveyQuestionsResponse
): QuestionGroup[] {
  if (!surveyResponse.questions || surveyResponse.questions.length === 0) {
    return [];
  }

  const groupedQuestions = groupQuestionsByStep(surveyResponse.questions);
  const questionGroups: QuestionGroup[] = [];

  // Sort steps to ensure proper order
  const sortedSteps = Array.from(groupedQuestions.keys()).sort((a, b) => a - b);

  sortedSteps.forEach(stepNumber => {
    const stepQuestions = groupedQuestions.get(stepNumber) || [];

    // Sort questions by displayOrder within each step
    const sortedQuestions = stepQuestions.sort((a, b) =>
      (a.displayOrder || 0) - (b.displayOrder || 0)
    );

    // Use groupTitle from first question if available, otherwise generate default title
    const groupTitle = stepQuestions.find(q => q.groupTitle)?.groupTitle ||
      `Step ${stepNumber}`;

    // Use iconKey from first question if available
    const iconKey = stepQuestions.find(q => q.iconKey)?.iconKey || undefined;

    // Check if multiple questions are allowed (if more than one question in step)
    const allowMultipleQuestions = stepQuestions.length > 1;

    const questionGroup: QuestionGroup = {
      title: groupTitle,
      iconKey,
      step: stepNumber,
      questions: sortedQuestions.map(mapQuestion),
      allowMultipleQuestions
    };

    questionGroups.push(questionGroup);
  });

  return questionGroups;
}

/**
 * Maps SurveyQuestionsResponse to GenericQuestionGroup[] with constraints support
 */
export function mapSurveyQuestionsToGenericQuestionGroups(
  surveyResponse: SurveyQuestionsResponse
): GenericQuestionGroup[] {
  const baseGroups = mapSurveyQuestionsToQuestionGroups(surveyResponse);

  return baseGroups.map(group => {
    // Collect constraints from questions in this group
    const groupConstraints: StepConstraint[] = [];
    const groupSkipIf: StepConstraint[] = [];

    group.questions.forEach(question => {
      const originalQuestion = surveyResponse.questions?.find(q => q.key === question.key);

      if (originalQuestion?.constraints) {
        groupConstraints.push(...originalQuestion.constraints.map(mapStepConstraint));
      }

      if (originalQuestion?.skipIf) {
        groupSkipIf.push(...originalQuestion.skipIf.map(mapStepConstraint));
      }
    });

    const genericGroup: GenericQuestionGroup = {
      ...group,
      constraints: groupConstraints.length > 0 ? groupConstraints : undefined,
      skipIf: groupSkipIf.length > 0 ? groupSkipIf : undefined
    };

    return genericGroup;
  });
}

/** Opérateurs du back (equal, notequal, in, notin — casse libre) vers ceux du moteur front. */
const OPERATOR_BY_BACKEND_CODE: Record<string, GenericOperator> = {
  equal: GenericOperator.Equals,
  equals: GenericOperator.Equals,
  notequal: GenericOperator.NotEquals,
  not_equals: GenericOperator.NotEquals,
  in: GenericOperator.In,
  notin: GenericOperator.NotIn,
  not_in: GenericOperator.NotIn,
};

const mapRuleConstraint = (constraint: EligibilityRuleDto["constraints"][number]): StepConstraint => ({
  questionKey: constraint.questionKey || "",
  operator: OPERATOR_BY_BACKEND_CODE[(constraint.operator || "equal").toLowerCase()] ?? GenericOperator.Equals,
  value: constraint.value,
  values: constraint.values ? convertValuesToStringOrNumber(constraint.values) : undefined,
});

/**
 * Règles d'éligibilité du questionnaire telles que le back les expose (valeurs déjà
 * traduites en clés de réponse), dans l'ordre de priorité du serveur : le moteur front
 * prend la première règle satisfaite, comme le serveur.
 */
export function mapSurveyEligibilityRules(surveyResponse: SurveyQuestionsResponse): EligibilityRule[] {
  return (surveyResponse.eligibilityRules ?? [])
    .slice()
    .sort((a, b) => a.priority - b.priority)
    .map((rule) => ({
      key: rule.key,
      constraints: rule.constraints.map(mapRuleConstraint),
      result: rule.result === "ELIGIBLE" ? "eligible" : "non-eligible",
      reason: rule.reason ?? undefined,
      message: rule.message ?? undefined,
    }));
}
